import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface CeremonyResult {
  id: string
  kind: string
  slots: number
  winners: number
  executed_at: string
  sbn_block_id: string | null
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return d }
}

export default function NomineeHistory() {
  const [ceremonies, setCeremonies] = useState<CeremonyResult[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`${CORE_API}/api/v1/draw/ceremonies/public`)
      .then((r) => r.ok ? r.json() : null)
      .then((json) => setCeremonies(json?.data?.ceremonies || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-cocoa">Draw History</h2>
          <p className="text-xs text-cocoa/60">Past ceremony results and SBN proof of integrity</p>
        </div>
        <Link to="/ceremonies" className="rounded-xl border border-cocoa/20 px-3 py-1.5 text-xs font-semibold text-cocoa hover:bg-sand/30">
          Public View →
        </Link>
      </header>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-cocoa border-t-transparent" />
        </div>
      ) : ceremonies.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand bg-white/70 p-8 text-center">
          <p className="text-sm text-cocoa/60">No ceremonies have happened yet.</p>
          <p className="mt-1 text-xs text-cocoa/40">Keep rallying — the first draw will appear here when it runs.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {ceremonies.map((c) => (
            <div key={c.id} className="rounded-2xl border border-sand/50 bg-white/70 p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${
                      c.kind === 'classroom' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {c.kind === 'classroom' ? 'Classroom' : 'Locker Room'}
                    </span>
                    <span className="text-xs text-cocoa/50">{formatDate(c.executed_at)}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-cocoa">
                    {c.winners} winner{c.winners !== 1 ? 's' : ''} selected from {c.slots} slot{c.slots !== 1 ? 's' : ''}
                  </p>
                </div>
                {c.sbn_block_id && (
                  <div className="rounded-lg bg-stone-100 px-3 py-1.5 font-mono text-[10px] text-stone-600">
                    SBN: {c.sbn_block_id.slice(0, 12)}...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
